import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { IconBookStack, IconSparkles, IconUser } from "./icons";
import { isAccountPrimaryRuntime, isWebRuntime } from "../../lib/runtime";
import { cn } from "../../lib/utils";
import { userFacingError } from "../../../shared/userFacingError";
import { cloudAuthDesktopV2, signInForCurrentRuntime } from "../../services/cloudAuthDesktopV2";
import { BookshelfDialog } from "./BookshelfDialog";

type Props = {
  loading: boolean;
  statusMessage?: string;
  onOpenBook: () => void;
  onNewProject: () => void;
  onSelectCloudBook: (bookId: string) => void;
  cloudOpeningBookId?: string | null;
  onCloudAuthChange?: (signedIn: boolean) => void;
  onImportBytes?: (bytes: Uint8Array) => Promise<void> | void;
};

function WelcomeAction({
  icon,
  title,
  description,
  hint,
  disabled,
  onClick,
  className,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  hint?: string;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-auto w-full items-start justify-start gap-3 rounded-none border-b border-border px-[1.15rem] py-[0.85rem] text-left whitespace-normal last:border-b-0 hover:bg-muted/50 focus-visible:bg-muted/50 [&_svg]:size-5",
        className
      )}
    >
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.9375rem] leading-[1.35] font-semibold text-foreground">
          {title}
        </span>
        <span className="mt-1 block text-[0.8125rem] leading-[1.45] text-muted-foreground">
          {description}
        </span>
      </span>
      {hint ? (
        <kbd className="mt-0.5 shrink-0 rounded border border-border bg-background px-1.5 py-0.5 font-sans text-[0.6875rem] font-medium text-muted-foreground">
          {hint}
        </kbd>
      ) : null}
    </Button>
  );
}

export function WelcomeScreen({
  loading,
  statusMessage,
  onOpenBook,
  onNewProject,
  onSelectCloudBook,
  cloudOpeningBookId = null,
  onCloudAuthChange,
  onImportBytes,
}: Props) {
  const web = isWebRuntime();
  const accountPrimary = isAccountPrimaryRuntime();
  const [cloudConfigured, setCloudConfigured] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authMessage, setAuthMessage] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);

  const refreshCloudState = useCallback(async () => {
    const configured = await cloudAuthDesktopV2.isWebCloudConfigured();
    setCloudConfigured(configured);
    if (!configured) {
      setSignedIn(false);
      setUserEmail(null);
      onCloudAuthChange?.(false);
      return;
    }
    await cloudAuthDesktopV2.restoreSessionFromStorage();
    if (cloudAuthDesktopV2.isSignedIn()) {
      setSignedIn(true);
      setUserEmail(cloudAuthDesktopV2.getUserEmail() || null);
      onCloudAuthChange?.(true);
      return;
    }
    setSignedIn(false);
    setUserEmail(null);
    onCloudAuthChange?.(false);
  }, [onCloudAuthChange]);

  useEffect(() => {
    void refreshCloudState();
  }, [refreshCloudState]);

  const onSignIn = async () => {
    setAuthBusy(true);
    setAuthMessage("");
    try {
      await signInForCurrentRuntime();
      if (cloudAuthDesktopV2.isSignedIn()) {
        await refreshCloudState();
        setAuthMessage(
          accountPrimary ? "Signed in. Open a book or start a new one." : "Signed in. You can open books from your online library."
        );
      } else if (web) {
        setAuthMessage("Continuing with your Just Write account…");
      }
    } catch (error) {
      setAuthMessage(userFacingError(error, "sign-in"));
    } finally {
      setAuthBusy(false);
    }
  };

  const onSignOut = () => {
    cloudAuthDesktopV2.fullSignOut();
    setSignedIn(false);
    setUserEmail(null);
    onCloudAuthChange?.(false);
    setAuthMessage("Signed out.");
  };

  return (
    <div
      data-theme="light-clean"
      className="relative flex h-full min-h-full flex-col items-center justify-center overflow-auto bg-background p-4 text-foreground"
      role="main"
      aria-busy={loading}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-primary/10 mask-[radial-gradient(ellipse_70%_45%_at_50%_0%,black,transparent_70%)]"
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-110 px-6 pt-11 pb-13">
        <header className="mb-8 text-center">
          <p className="mb-3 text-[0.6875rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            Just Write
          </p>
          <h1 className="mb-2 text-[1.75rem] font-semibold tracking-[-0.03em] text-foreground">
            Start writing
          </h1>
          <p className="mx-auto max-w-[20rem] text-[0.875rem] leading-6 text-muted-foreground">
            {accountPrimary
              ? signedIn
                ? "Open a book from your bookshelf, or start something new."
                : "Sign in to open your books or start a new one."
              : web
                ? "Open a book from your library, or start something new."
                : "Open a project on this computer, create a new book, or sign in to your library."}
          </p>
        </header>

        {loading ? (
          <p className="text-center text-sm text-muted-foreground" role="status">
            Loading your last session…
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {!web && !accountPrimary ? (
              <Card
                role="region"
                aria-label="On this computer"
                className="gap-0 border border-border py-0 shadow-sm ring-0"
              >
                <CardHeader className="rounded-none border-b border-border bg-muted/25 px-[1.15rem] py-3">
                  <CardTitle className="text-[0.6875rem] leading-normal font-semibold tracking-widest text-muted-foreground uppercase">
                    On this computer
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <WelcomeAction
                    icon={<IconBookStack />}
                    title="Open book"
                    description="Open a Just Write project from disk"
                    hint="⌘O"
                    onClick={onOpenBook}
                  />
                  <WelcomeAction
                    icon={<IconSparkles />}
                    title="New project"
                    description="Start a fresh book with a blank first chapter"
                    hint="⌘N"
                    onClick={onNewProject}
                  />
                </CardContent>
              </Card>
            ) : null}

            <Card
              role="region"
              aria-label={accountPrimary ? "Your account" : "Your library"}
              className="gap-0 border border-border py-0 shadow-sm ring-0"
            >
              <CardHeader className="rounded-none border-b border-border bg-muted/25 px-[1.15rem] py-3">
                <CardTitle className="text-[0.6875rem] leading-normal font-semibold tracking-widest text-muted-foreground uppercase">
                  {accountPrimary ? "Your account" : "Your library"}
                </CardTitle>
                {signedIn && userEmail ? (
                  <p
                    className="mt-1 truncate text-xs leading-[1.4] text-muted-foreground"
                    title={userEmail}
                  >
                    {userEmail}
                  </p>
                ) : null}
              </CardHeader>

              <CardContent className="p-0">
                {signedIn ? (
                  <div>
                    <WelcomeAction
                      icon={<IconBookStack />}
                      title={accountPrimary ? "Open" : "Open from library"}
                      description={
                        accountPrimary
                          ? "Browse books in your account"
                          : "Browse books synced to your Just Write account"
                      }
                      hint="⌘O"
                      onClick={() => {
                        setAuthMessage("");
                        setShowLibrary(true);
                      }}
                    />
                    {accountPrimary || web ? (
                      <WelcomeAction
                        icon={<IconSparkles />}
                        title={accountPrimary ? "New" : "New project"}
                        description={
                          accountPrimary
                            ? "Create a new book in your account"
                            : "Start a fresh book, then sync when you save"
                        }
                        hint="⌘N"
                        onClick={onNewProject}
                      />
                    ) : null}
                  </div>
                ) : (
                  <WelcomeAction
                    icon={<IconUser />}
                    title={authBusy ? "Opening sign-in…" : "Sign in"}
                    description={
                      cloudConfigured
                        ? accountPrimary
                          ? "Continue with your Just Write account"
                          : web
                            ? "Continue with your heyjustwrite.com account"
                            : "Sign in to open and sync books from the web"
                        : web
                          ? "Sign-in is unavailable in this build"
                          : "Set DESKTOP_WEB_APP_URL in apps/desktop-v2/.env, then restart"
                    }
                    disabled={authBusy || !cloudConfigured}
                    onClick={() => void onSignIn()}
                  />
                )}
              </CardContent>

              {signedIn ? (
                <CardFooter className="justify-end bg-muted/15 px-2 py-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={onSignOut}
                  >
                    Sign out
                  </Button>
                </CardFooter>
              ) : null}
            </Card>
          </div>
        )}

        {authMessage ? (
          <p className="mt-5 text-center text-[0.8125rem] leading-5 text-foreground" role="status">
            {authMessage}
          </p>
        ) : null}
        {!loading && statusMessage && !authMessage ? (
          <p className="mt-5 text-center text-[0.8125rem] leading-5 text-muted-foreground" role="status">
            {statusMessage}
          </p>
        ) : null}
      </div>

      <BookshelfDialog
        open={showLibrary}
        onClose={() => {
          if (cloudOpeningBookId) return;
          setShowLibrary(false);
        }}
        onSelectBook={(bookId) => {
          onSelectCloudBook(bookId);
        }}
        openingBookId={cloudOpeningBookId}
        onImportBytes={onImportBytes}
      />
    </div>
  );
}
